// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { Box, Collapse, IconButton, Typography } from "@mui/material"
import { FC, useContext, useEffect, useState } from "react"
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
// component import
import { Alert } from '../common/Alert'
import { CustomController } from '../common/CustomController'
import { CustomModal } from "../common/CustomModal"
// others
import { TaskType, UpdateTaskInput, useUpdateTaskMutation } from '../../../generated'
import { GENERAL_INFORMATION } from "../../constants"
import { AppContext, AuthContext } from "../../context"
import { ActionType } from "../../context/AppContextReducer"
import { ErrorException, ViewCurrentCustomTaskProps } from "../../interfaceTypes"
import { titleize } from "../../utils"
import { createCustomTaskValidationSchema } from '../../validationSchema'

export const ViewCurrentCustomTask: FC<ViewCurrentCustomTaskProps> = ({ refetch, loading, currentCustomTask, setCurrentCustomTask }) => {
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const { isEdit, dispatch: appDispatch } = useContext(AppContext)
  const { isAdmin } = useContext(AuthContext)
  const { id, title } = currentCustomTask || {}

  const handleClose = () => {
    appDispatch({ type: ActionType.SET_IS_EDIT, isEdit: false })
    setIsEditOpen(false)
    reset()
  }

  const methods = useForm<UpdateTaskInput>({
    mode: 'all',
    resolver: yupResolver(createCustomTaskValidationSchema),

    defaultValues: {
      title: titleize(title || ''),
    }
  })

  const { handleSubmit, reset, setValue } = methods;

  const [updateTask, { loading: isLoading }] = useUpdateTaskMutation({
    onError(error) {
      const { graphQLErrors } = error

      if (graphQLErrors) {
        graphQLErrors.map(
          // eslint-disable-next-line
          ({ extensions }) => {
            if (extensions) {
              const { exception } = extensions;
              if (exception) {
                const { response } = exception as ErrorException
                const { response: errorResponse } = response || {}
                if (errorResponse) {
                  const { error } = errorResponse
                  Alert.error(error)
                }
              }
            }
          })
      }
    },

    onCompleted(data) {
      if (data) {
        const { updateTask: { response, task } } = data || {}

        if (response) {
          const { status } = response

          if (status === 200 && task) {
            refetch && refetch()
            setCurrentCustomTask(task)
            Alert.success('Custom task updated successfully')
            reset()
            setIsEditOpen(false);
          }
        }
      }
    }
  })

  const onSubmit: SubmitHandler<UpdateTaskInput> = async (data: UpdateTaskInput) => {
    const { title } = data

    if (id) {
      await updateTask({
        variables: {
          updateTaskInput: {
            title,
            type: TaskType.Custom,
            id
          }
        }
      })
    }
  }

  useEffect(() => {
    if (!isEditOpen && !isLoading && !loading && isEdit) {
      title && setValue("title", title);
    }

  }, [loading, title, setValue, isEditOpen, isLoading, isEdit])

  return (
    <CustomModal title="Custom Task detail" isOpen={isEdit} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" justifyContent="space-between" alignItems='center'>
            <Typography variant="body2" fontSize='18px' fontWeight='medium'>
              {GENERAL_INFORMATION}
            </Typography>

            {isAdmin &&
              <Box>
                <IconButton color="primary" onClick={() => setIsEditOpen(!isEditOpen)} aria-label={isEditOpen ? 'back' : 'edit'}>
                  {isEditOpen ? <ArrowBackIcon /> : <EditIcon />}
                </IconButton>

                {isEditOpen &&
                  <IconButton color="primary" type='submit' disabled={isLoading} aria-label='save'>
                    <SaveIcon />
                  </IconButton>
                }
              </Box>
            }
          </Box>

          <Box marginTop={2} marginBottom={3}>
            <Collapse in={!isEditOpen}>
              <Typography variant="body2" fontSize='14px'>Title:</Typography>

              <Typography variant="h6" fontSize='16px' fontWeight='normal' textTransform='capitalize'>
                {titleize(title || '')}
              </Typography>
            </Collapse>

            <Collapse in={isEditOpen}>
              <CustomController
                controllerName="title"
                controllerLabel="Title"
                fieldType="text"
              />
            </Collapse>
          </Box>
        </form>
      </FormProvider>
    </CustomModal>
  )
}